import React, { useEffect, useState, useMemo } from "react";
import {
	Row, Col, Button, ButtonGroup, Card, CardHeader, CardBody, Spinner
} from "reactstrap";
import Select from "react-select";
import { Template } from "store/templates/types";

/**
 * Replace {{variable}} in template with given values.
 * Handles HTML or plain text.
 */
export function replaceVars(template: string, vars: Record<string, string | undefined>) {
	if (!template) return "";
	return template.replace(/{{\s*([^}]+)\s*}}/g, (_, key) => {
		const v = vars[key.trim()];
		// If variable missing, show as-is (or replace with empty string if you prefer)
		return v === undefined ? `{{${key}}}` : v;
	});
}

const recipientOptions = [
	{
		value: "1",
		label: "Alice Johnson",
		vars: {
			name: "Alice Johnson",
			first_name: "Alice",
			last_name: "Johnson",
			country: "India",
			gender: "Female",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		value: "2",
		label: "Bob Smith",
		vars: {
			name: "Bob Smith",
			first_name: "Bob",
			last_name: "Smith",
			country: "Nepal",
			gender: "Male",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		value: "3",
		label: "Carlos Martinez",
		vars: {
			name: "Carlos Martinez",
			first_name: "Carlos",
			last_name: "Martinez",
			country: "Mexico",
			gender: "Male",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		value: "4",
		label: "Priya Patel",
		vars: {
			name: "Priya Patel",
			first_name: "Priya",
			last_name: "Patel",
			country: "UK",
			gender: "Female",
			AGENCY_NAME: "Your Consultants"
		}
	}
];


interface StepPreviewProps {
	data: any; // Not used anymore, unless you want to keep for future
	channels: any[]; // [{channel: "EMAIL", template_summary, install_summary}]
	setData: (d: any) => void; // Not used here, unless you want to keep for future
}
const StepPreview: React.FC<StepPreviewProps> = ({
	channels
}) => {
	const enabledChannels = channels.map((c) => c.channel);
	const [active, setActive] = useState<string>(enabledChannels[0] || "EMAIL");
	const [loading, setLoading] = useState(false);
	const [recipient, setRecipient] = useState(recipientOptions[0]);

	// Memoize for performance
	const current = useMemo(() => channels.find((c) => c.channel === active), [channels, active]);

	const templateContent = current?.template_content;

	const sampleVars = recipient?.vars || {};

	let htmlPreview = null, titlePreview = null;
	if (active === "WHATSAPP" && templateContent && typeof templateContent === "object") {
		htmlPreview = applyWhatsappMapping(templateContent.data, templateContent.mapping, sampleVars);
	} else if (templateContent) {
		htmlPreview = replaceVars(
			typeof templateContent === "string"
				? templateContent
				: (templateContent.template || templateContent.body || templateContent.html || ""),
			sampleVars
		);
		titlePreview = templateContent && typeof templateContent === "object"
			? replaceVars(templateContent.title, sampleVars)
			: null;
	}

	function applyWhatsappMapping(templateText: any, mappingArr: any, recipientVars: { [x: string]: any; name?: string; first_name?: string; last_name?: string; country?: string; gender?: string; AGENCY_NAME?: string; }) {
		let result = templateText;
		(mappingArr || []).forEach((m: { placeholder: string; mappedTo: { fieldKey: any; }; }, idx: number) => {
		  const token = m.placeholder || `{{${idx + 1}}}`;
		  const field = m.mappedTo?.fieldKey;
		  const value = recipientVars[field] ?? token;
		  result = result.split(token).join(value);
		});
		return result;
	}
	return (
		<>
			<Row className="align-items-center mb-3">
				<Col xs={12} sm={6}>
					<Select
						options={recipientOptions}
						placeholder="Sample recipient…"
						isSearchable={false}
						getOptionLabel={(opt: { label: any; }) => opt.label}
						getOptionValue={(opt: { value: any; }) => opt.value}
						value={recipient}
						onChange={(opt: any) => setRecipient(opt as any)}
					/>
				</Col>
				{enabledChannels.length > 1 && (
					<Col xs={12} sm={6} className="text-sm-end mt-2 mt-sm-0">
						<ButtonGroup>
							{enabledChannels.map((ch) => {
								const channelObj = channels.find(c => c.channel === ch);
								return (
									<Button
										key={ch}
										outline
										color="primary"
										active={active === ch}
										onClick={() => setActive(ch)}
										style={{ minWidth: 80 }}
									>
										{channelObj?.install_summary?.logo && (
											<img src={channelObj.install_summary.logo} alt="" width={16} height={16} className="me-1" />
										)}
										<span className="d-none d-md-inline">
											{channelObj?.install_summary?.app_name || ch}
										</span>
									</Button>
								)
							})}
						</ButtonGroup>
					</Col>
				)}
			</Row>

			<Card className="mb-3 shadow-sm">
				<CardHeader className="bg-white d-flex align-items-center">
					{current?.install_summary?.logo && (
						<img src={current.install_summary.logo} alt="" width={20} height={20} className="me-2" />
					)}
					Viewing: <strong className="ms-1">{current?.install_summary?.app_name || active}</strong>
					{current?.template_summary?.name && (
						<span className="ms-2 text-muted small">{current.template_summary.name}</span>
					)}
				</CardHeader>
				<CardBody style={{ minHeight: 180, position: "relative" }}>
					{loading ? (
						<div className="text-center py-5">
							<Spinner size="lg" />
						</div>
					) : active === "EMAIL" && htmlPreview ? (
						<div style={{ overflowY: "auto", maxHeight: 300 }} dangerouslySetInnerHTML={{ __html: htmlPreview }} />
					) : (active === "SMS" || active === "WHATSAPP") && htmlPreview ? (
						<pre className="m-0" style={{ whiteSpace: "pre-wrap" }}>{htmlPreview}</pre>
					) : active === "IN_APP" && templateContent ? (
						<div>
							<h5>{titlePreview}</h5>
							<p>{htmlPreview}</p>
						</div>
					) : (
						<div className="text-muted">No template selected or no content available.</div>
					)}
				</CardBody>
			</Card>
		</>
	);
};

export default StepPreview;
